/**
 * 错误处理工具类
 * 用于统一处理应用中的错误，包括弹窗提示、日志记录和错误恢复
 */
export class ErrorHandler {
  
  // 错误类型枚举
  static ERROR_TYPES = {
    NETWORK: 'NETWORK_ERROR',
    VALIDATION: 'VALIDATION_ERROR',
    SECURITY: 'SECURITY_ERROR',
    STORAGE: 'STORAGE_ERROR',
    AI_SERVICE: 'AI_SERVICE_ERROR',
    UNKNOWN: 'UNKNOWN_ERROR'
  }

  // 错误严重程度
  static SEVERITY_LEVELS = {
    LOW: 'low',
    MEDIUM: 'medium',
    HIGH: 'high',
    CRITICAL: 'critical'
  }

  // 错误日志存储
  static errorLog = []
  static maxLogSize = 100

  /**
   * 记录错误到日志
   * @param {Error} error - 错误对象
   * @param {string} context - 错误上下文
   * @param {string} severity - 错误严重程度
   */
  static logError(error, context = '', severity = this.SEVERITY_LEVELS.MEDIUM) {
    const errorEntry = {
      timestamp: new Date().toISOString(),
      message: error.message,
      stack: error.stack,
      context,
      severity,
      userAgent: navigator.userAgent,
      url: window.location.href
    }
    
    this.errorLog.push(errorEntry)
    
    // 保持日志大小限制
    if (this.errorLog.length > this.maxLogSize) {
      this.errorLog.shift()
    }
    
    // 在开发环境下输出到控制台
    if (import.meta.env.DEV) {
      console.error('🚨 错误记录:', errorEntry)
    }
    
    // 严重错误发送到监控服务（如果有）
    if (severity === this.SEVERITY_LEVELS.CRITICAL) {
      this.reportCriticalError(errorEntry)
    }
  }

  /**
   * 报告严重错误到监控服务
   * @param {Object} errorEntry - 错误条目
   */
  static reportCriticalError(errorEntry) {
    // 这里可以集成第三方错误监控服务
    // 如 Sentry, Bugsnag 等
    console.error('🚨 严重错误需要关注:', errorEntry)
  }

  /**
   * 获取错误日志
   * @returns {Array} 错误日志数组
   */
  static getErrorLog() {
    return [...this.errorLog]
  }

  /**
   * 清空错误日志
   */
  static clearErrorLog() {
    this.errorLog = []
  }
  
  /**
   * 显示错误弹窗
   * @param {string} title - 错误标题
   * @param {string} message - 错误消息
   * @param {string} details - 详细错误信息（可选）
   */
  static showError(title, message, details = '') {
    // 创建错误弹窗元素
    const errorModal = document.createElement('div')
    errorModal.className = 'error-modal'
    errorModal.innerHTML = `
      <div class="error-modal-overlay">
        <div class="error-modal-content">
          <div class="error-modal-header">
            <h3 class="error-modal-title">${title}</h3>
            <button class="error-modal-close" onclick="this.closest('.error-modal').remove()">×</button>
          </div>
          <div class="error-modal-body">
            <p class="error-modal-message">${message}</p>
            ${details ? `<details class="error-modal-details">
              <summary>详细错误信息</summary>
              <pre>${details}</pre>
            </details>` : ''}
          </div>
          <div class="error-modal-footer">
            <button class="error-modal-btn error-modal-btn-primary" onclick="this.closest('.error-modal').remove()">
              确定
            </button>
          </div>
        </div>
      </div>
    `
    
    // 添加样式
    const style = document.createElement('style')
    style.textContent = `
      .error-modal {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 10000;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      }
      
      .error-modal-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
      }
      
      .error-modal-content {
        background: white;
        border-radius: 8px;
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
        max-width: 500px;
        width: 100%;
        max-height: 80vh;
        overflow-y: auto;
      }
      
      .error-modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 20px 20px 0 20px;
        border-bottom: 1px solid #e5e7eb;
      }
      
      .error-modal-title {
        margin: 0;
        color: #dc2626;
        font-size: 18px;
        font-weight: 600;
      }
      
      .error-modal-close {
        background: none;
        border: none;
        font-size: 24px;
        color: #6b7280;
        cursor: pointer;
        padding: 0;
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
      }
      
      .error-modal-close:hover {
        background: #f3f4f6;
        color: #374151;
      }
      
      .error-modal-body {
        padding: 20px;
      }
      
      .error-modal-message {
        margin: 0 0 15px 0;
        color: #374151;
        line-height: 1.5;
      }
      
      .error-modal-details {
        margin-top: 15px;
      }
      
      .error-modal-details summary {
        cursor: pointer;
        color: #6b7280;
        font-size: 14px;
        margin-bottom: 10px;
      }
      
      .error-modal-details pre {
        background: #f9fafb;
        border: 1px solid #e5e7eb;
        border-radius: 4px;
        padding: 10px;
        font-size: 12px;
        color: #374151;
        overflow-x: auto;
        white-space: pre-wrap;
        word-break: break-word;
      }
      
      .error-modal-footer {
        padding: 0 20px 20px 20px;
        display: flex;
        justify-content: flex-end;
      }
      
      .error-modal-btn {
        padding: 8px 16px;
        border: none;
        border-radius: 4px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s;
      }
      
      .error-modal-btn-primary {
        background: #dc2626;
        color: white;
      }
      
      .error-modal-btn-primary:hover {
        background: #b91c1c;
      }
    `
    
    // 添加样式到页面
    if (!document.querySelector('#error-modal-styles')) {
      style.id = 'error-modal-styles'
      document.head.appendChild(style)
    }
    
    // 添加到页面
    document.body.appendChild(errorModal)
    
    // 点击遮罩层关闭弹窗
    errorModal.querySelector('.error-modal-overlay').addEventListener('click', (e) => {
      if (e.target === e.currentTarget) {
        errorModal.remove()
      }
    })
    
    // ESC键关闭弹窗
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        errorModal.remove()
        document.removeEventListener('keydown', handleEsc)
      }
    }
    document.addEventListener('keydown', handleEsc)
  }
  
  /**
   * 处理故事生成错误
   * @param {Error} error - 错误对象
   * @param {string} content - 用户输入内容
   * @param {string} genre - 故事类型
   * @param {string} style - 故事风格
   */
  static handleStoryGenerationError(error, content, genre, style) {
    console.error('故事生成错误:', error)
    
    let title = '故事生成失败'
    let message = '很抱歉，故事生成过程中出现了错误。'
    let details = ''
    
    if (error.message.includes('API未配置')) {
      title = 'API配置错误'
      message = '请先配置API密钥和地址，然后重试。'
    } else if (error.message.includes('JSON修复失败')) {
      title = '数据格式错误'
      message = 'AI返回的数据格式不正确，无法解析。这可能是由于AI模型输出格式不稳定导致的。'
      details = `错误详情: ${error.message}\n\n建议:\n1. 检查API配置是否正确\n2. 尝试重新生成\n3. 如果问题持续，请检查API服务状态`
    } else if (error.message.includes('数据格式验证失败')) {
      title = '数据验证失败'
      message = 'AI返回的数据不符合预期格式，缺少必要的字段。'
      details = `验证错误: ${error.message}\n\n这通常表示AI模型没有按照要求的格式返回数据。`
    } else if (error.message.includes('API响应中未找到有效的JSON格式')) {
      title = '响应格式错误'
      message = 'AI返回的响应中不包含有效的JSON数据。'
      details = `错误详情: ${error.message}\n\n可能的原因:\n1. API服务异常\n2. 网络连接问题\n3. API密钥无效`
    } else {
      details = `错误类型: ${error.constructor.name}\n错误消息: ${error.message}\n\n输入内容: ${content?.substring(0, 100)}...\n故事类型: ${genre}\n故事风格: ${style}`
    }
    
    this.showError(title, message, details)
  }
  
  /**
   * 处理通用错误
   * @param {Error} error - 错误对象
   * @param {string} context - 错误上下文
   */
  static handleGenericError(error, context = '') {
    console.error('通用错误:', error)
    
    const title = '操作失败'
    const message = `操作过程中出现了错误${context ? ` (${context})` : ''}。`
    const details = `错误类型: ${error.constructor.name}\n错误消息: ${error.message}`
    
    this.showError(title, message, details)
  }
}
